import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Typography, Button, Tooltip } from '@mui/material_modern'
import { useGetCustomerQuery } from 'services/customers'
import InputV2 from 'components/generic/form/InputV2'
import Select from 'components/generic/form/Select'
import RadioGroup from 'components/generic/form/RadioGroup'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import { useSelector } from 'react-redux'
import { objectDetailsToDropdownFormatter } from 'utils/data-manipulation/common'
import { isLocationInYardList } from 'utils/helpers'
import DatePickerV2 from 'components/generic/form/DatePickerV2'
import PrintDialog from 'components/generic/printable/PrintDialog'
import ContainerQRCodeTag from 'components/generic/printable/ContainerQRCodeTag'
import { PRINT_DIALOG_TYPES } from 'utils/constants'

// Function to generate random alphanumeric code
const generateRandomCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

const ContainerProperties = ({ methods, assetData, assetTypesData, canEditLocation }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    getValues,
  } = methods

  const { enableDispatchAssetBarcodes } = useSelector(state => state.featureFlags?.variables || {})

  const [customerId, setCustomerId] = useState('')
  const [locationOptions, setLocationOptions] = useState([])
  // StoredYardLocationId allows us to change the locationId in the form without losing the original selected yard
  const [storedYardLocationId, setStoredYardLocationId] = useState('')
  const [showPrintBarcodeModal, setShowPrintBarcodeModal] = useState(false)

  const selectedLocationType = watch('locationType')
  const selectedCustomer = watch('customer')
  const locationId = watch('locationId')
  const barcodeValue = watch('barcodeNumber') || ''
  const assetName = watch('assetName') || ''

  const { locationId: yardLocationId } = useSelector(state => state.settings.details)
  const { inOrgMode } = useSelector(state => state.ui)
  const { yardDetailsList, yardId } = useSelector(state => state.auth)
  const { data: customerData } = useGetCustomerQuery(
    { customerId },
    { refetchOnMountOrArgChange: true, skip: !customerId },
  )

  const yardSelected = useMemo(() => isLocationInYardList(locationId, yardDetailsList), [yardDetailsList, locationId])

  const yardOptions = useMemo(() => {
    return (
      yardDetailsList?.map((item, index) =>
        objectDetailsToDropdownFormatter(item, index, {
          idKey: item.defaultLocationId ? 'defaultLocationId' : 'locationId',
        }),
      ) || []
    )
  }, [yardDetailsList])

  const setLocation = (newLocationId, shouldSaveYardLocation = false, shouldDirty = true) => {
    setValue('locationId', newLocationId || '', { shouldDirty })

    // This updates our local variable for ui purposes
    if (shouldSaveYardLocation) {
      setStoredYardLocationId(newLocationId || '')
    }

    // Update the effective date to today
    if (locationId && locationId !== newLocationId) {
      setValue('assetStatus.effectiveDate', new Date())
    }
  }

  const setCustomerValue = (customer, asset, shouldDirty = true) => {
    setValue('customer', customer || null, { shouldDirty })
    setLocationOptions(customer?.locations || [])
    setLocation(
      customer?.locations?.find(location => location.id === asset?.assetStatus?.currentLocation?.id)?.id ||
        customer?.locations?.[0]?.id ||
        {},
      shouldDirty,
    )
  }

  useEffect(() => {
    if (assetData) {
      setCustomerId(assetData?.assetStatus?.currentLocation?.customerId)

      if (inOrgMode) {
        // For assets with yard location selected
        if (
          isLocationInYardList(assetData?.assetStatus?.currentLocation?.id, yardDetailsList) &&
          selectedLocationType === 'yard-location'
        ) {
          setStoredYardLocationId(assetData?.assetStatus?.currentLocation?.id)
        } else if (yardDetailsList?.length > 0) {
          // For assets with customer location selected
          setStoredYardLocationId(yardDetailsList[0]?.locationId)
        }
      }
    } else if (yardDetailsList?.length > 0) {
      // For new assets with no assetData prop
      setLocation(yardDetailsList[0]?.locationId, true)
    }
  }, [assetData])

  useEffect(() => {
    if (customerData === undefined) return
    setCustomerValue(customerData, assetData, false)
  }, [customerData, assetData])

  // Handle generate random barcode
  const handleGenerateBarcode = () => {
    setValue('barcodeNumber', generateRandomCode(), { shouldDirty: true })
  }

  // Handle print barcode
  const handleOpenPrintDialog = () => {
    setShowPrintBarcodeModal(true)
  }

  const handleClosePrintDialog = () => {
    setShowPrintBarcodeModal(false)
  }

  // Get container type name for printing
  const getContainerTypeName = () => {
    const typeId = getValues('assetTypeId')
    const containerType = assetTypesData?.find(type => type.id === typeId)
    return containerType?.name || ''
  }

  return (
    <>
      <Stack key="container-properties" flexGrow={1} ml={4} spacing={2}>
        <Typography variant="h6">Container Properties</Typography>
        <Box width={{ xs: '100%', md: '50%' }}>
          <InputV2
            name="assetName"
            label="Container Name"
            data-test="container-asset-name"
            size="small"
            defaultValue=""
            control={control}
            error={!!errors?.assetName}
            helperText={errors?.assetName?.message}
            onChange={() => clearErrors('assetName')}
          />
        </Box>
        <Box width={{ xs: '100%', md: '50%' }}>
          <Select
            name="assetTypeId"
            label="Container Type"
            control={control}
            error={!!errors?.assetTypeId}
            variant="outlined"
            size="small"
            data-testid="asset-type-id"
            defaultValue={assetTypesData?.[0]?.id || ''}
            items={assetTypesData || []}
            helperText={errors?.assetTypeId?.message}
          />
        </Box>
        {enableDispatchAssetBarcodes && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <Stack direction="column" spacing={1}>
              <Box display="flex" alignItems="flex-start">
                <Box flexGrow={1}>
                  <InputV2
                    name="barcodeNumber"
                    label="Barcode"
                    data-test="container-asset-barcode"
                    size="small"
                    defaultValue={assetData?.barcodeNumber || ''}
                    control={control}
                    error={!!errors?.barcodeNumber}
                    helperText="Enter an existing barcode number or generate a new one"
                    InputProps={{
                      endAdornment: (
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Generate a random barcode number">
                            <Button size="small" variant="outlined" color="primary" onClick={handleGenerateBarcode}>
                              GENERATE
                            </Button>
                          </Tooltip>
                        </Stack>
                      ),
                    }}
                  />
                </Box>
                {barcodeValue && (
                  <Box ml={2} mt={0.5}>
                    <Button size="small" variant="outlined" color="primary" onClick={handleOpenPrintDialog}>
                      PRINT
                    </Button>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>
        )}
        <Box width={{ xs: '100%' }}>
          <InputV2
            defaultValue=""
            name="notes"
            label="Notes"
            data-test="container-asset-notes"
            size="small"
            control={control}
            error={errors?.notes}
          />
        </Box>
      </Stack>
      <Stack key="container-locations" flexGrow={1} ml={4} mt={3} spacing={2}>
        <Typography variant="h6">Container Locations</Typography>
        <Box pl={1} width={{ xs: '100%', md: '50%' }}>
          <RadioGroup
            name="locationType"
            label=""
            control={control}
            onChange={({ target: { value } }) => {
              // When switching between yard and customer location radios, the location
              // and customer need to be set to null
              setValue('customer', null, { shouldDirty: true })

              if (value === 'yard-location') {
                setLocation(storedYardLocationId)
                setLocationOptions([])
                if (!inOrgMode) {
                  setLocation(yardLocationId)
                }
              } else {
                setLocation(null)
              }
            }}
            defaultValue="yard-location"
            disabled={!canEditLocation}
            items={[
              {
                id: 'yard-location',
                name: 'In Yard',
              },
              {
                id: 'customer-location',
                name: 'Customer Location',
              },
            ]}
          />
        </Box>
        {selectedLocationType === 'yard-location' && inOrgMode && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <Select
              name="locationId"
              label="Yard"
              control={control}
              variant="outlined"
              size="small"
              selectProps={{
                onChange: ({ target: { value } }) => {
                  setLocation(value, true)
                },
              }}
              items={yardOptions}
              helperText={errors?.yardId?.message || (yardSelected ? '' : 'Yard needs to be selected')}
              defaultValue={inOrgMode ? '' : yardId}
              disabled={!canEditLocation || !inOrgMode}
            />
          </Box>
        )}
        {selectedLocationType === 'customer-location' && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <CustomerAutocomplete
              name="customer"
              label="Customer Name"
              variant="outlined"
              size="small"
              defaultValue={null}
              freeSolo={false}
              onChange={customer => setCustomerValue(customer)}
              control={control}
              autocompleteProps={{
                renderOption: (props, option) => (
                  <li {...props} key={option.id}>
                    {option.displayName}
                  </li>
                ),
              }}
              disabled={!canEditLocation || selectedLocationType !== 'customer-location'}
            />
          </Box>
        )}
        {selectedLocationType === 'customer-location' && (
          <Box width={{ xs: '100%', md: '50%' }}>
            <Select
              name="locationId"
              label="Location"
              control={control}
              variant="outlined"
              size="small"
              selectProps={{
                onChange: ({ target: { value } }) => {
                  setLocation(value)
                },
              }}
              items={locationOptions}
              helperText={
                errors?.locationId?.message ||
                (selectedLocationType === 'customer-location' && !selectedCustomer
                  ? 'Customer needs to be selected'
                  : '')
              }
              defaultValue=""
              disabled={!canEditLocation || selectedLocationType !== 'customer-location' || !selectedCustomer}
            />
          </Box>
        )}
        <Box mt={3} ml={4} width={{ xs: '100%', md: '50%' }}>
          <DatePickerV2
            name="assetStatus.effectiveDate"
            label="At Location Since"
            data-test="asset-blade-effective-date"
            variant="outlined"
            size="small"
            control={control}
            defaultDate={
              !assetData
                ? new Date()
                : new Date(assetData?.assetStatus?.effectiveDate || new Date(assetData?.assetStatus?.createdAt))
            }
            InputLabelProps={{ shrink: true }}
            datePickerProps={{ maxDate: new Date(), disabled: !canEditLocation }}
            disabled={!canEditLocation}
          />
        </Box>
      </Stack>

      {/* Print Dialog for QR Code */}
      {showPrintBarcodeModal && (
        <PrintDialog
          title=""
          open={showPrintBarcodeModal}
          onClose={handleClosePrintDialog}
          dialogType={PRINT_DIALOG_TYPES.TAG}
          showLogoAddress={false}
        >
          <ContainerQRCodeTag
            containerName={assetName}
            barcodeNumber={barcodeValue}
            containerType={getContainerTypeName()}
          />
        </PrintDialog>
      )}
    </>
  )
}

ContainerProperties.propTypes = {
  methods: PropTypes.object,
  assetData: PropTypes.object,
  assetTypesData: PropTypes.array,
  canEditLocation: PropTypes.bool,
}

ContainerProperties.defaultProps = {
  methods: {},
  assetData: null,
  assetTypesData: [],
  canEditLocation: false,
}

export default ContainerProperties
